import CLIENT from '../utils/restClient';
import Logger, {ALL_MESSAGES, LOG_LEVELS} from '../utils/logger';
import { ACTION_TYPES as FILE_ACTIONS } from './files';

export const MODULE_NAME = 'UPLOADED_DOCUMENTS_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
  return MODULE_NAME+'.'+name;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const ACTION_OK = namespaced('ACTION_OK');
const ACTION_FAIL = namespaced('ACTION_FAIL');
const LOAD_DETAILS  = namespaced('LOAD_DETAILS');
const LOAD_DETAILS_OK  = namespaced('LOAD_DETAILS_OK');
const LOAD_DETAILS_FAIL  = namespaced('LOAD_DETAILS_FAIL');
const UNLOAD_DETAILS = namespaced('UNLOAD_DETAILS');

export const ACTION_TYPES = {
  LOAD: LOAD,
  LOAD_OK: LOAD_OK,
  LOAD_FAIL: LOAD_FAIL,
  ACTION_OK: ACTION_OK,
  ACTION_FAIL: ACTION_FAIL,
  LOAD_DETAILS: LOAD_DETAILS,
  LOAD_DETAILS_OK: LOAD_DETAILS_OK,
  LOAD_DETAILS_FAIL: LOAD_DETAILS_FAIL,
  UNLOAD_DETAILS: UNLOAD_DETAILS,
};

function getFileProcessedStatusFromUploadStatus(status) {
  switch (status) {
    case FILE_ACTIONS.FILE_UPLOAD:
      return 'Uploading...';
    case FILE_ACTIONS.FILE_UPLOAD_OK:
      return 'Queued...';
    case FILE_ACTIONS.FILE_UPLOAD_FAIL:
      return 'Failed to upload...';
    default:
      return 'Unknown...'
  }
}

/**
 * Cleans up and prepares the file list to show correct status
 *
 * @param {*} uploadedFiles: Files uploaded
 * @param {*} processedFiles: Files being processed and their status
 */
function scrubUploadedFiles(uploadedFiles, processedFiles) {
  log.debug('scrubUploadedFiles: ', uploadedFiles, processedFiles );
  uploadedFiles = uploadedFiles || [];
  processedFiles = processedFiles || [];

  //Returns true if the given uploaded file is in process; otherwise false
  const isBeingProcessed = ( uploadedFilename ) => {
    return processedFiles.some( (file) =>{
       return file.fileName.includes(uploadedFilename);
    });
  };

  //Clean up the uploaded files to show correct status
  const filteredUploadedFiles = uploadedFiles.filter( (uploadedFile) => {
    //Check if a filename has extension
    const lastIndexOfPeriod = uploadedFile.fileName.lastIndexOf('.');
    //Remove files without extensions; as files without extensions are not supported
    if(lastIndexOfPeriod < 0) {
      return false;
    }
    //All file extensions are eventually converted to xml, we need to match with filename without extension
    const uploadFileNameWithoutExtension = uploadedFile.fileName.substring(0, lastIndexOfPeriod);
    const isFileInProcess = isBeingProcessed(uploadFileNameWithoutExtension);
    //Filter out files under process or failed to upload
    return !isFileInProcess && uploadedFile.processingDate !== 'Failed to upload...';
  });

  //Return the filtered list of files to be shown
  return filteredUploadedFiles;
}

function addToUploadedFiles(uploadedFiles, processedFiles, file, status) {
  log.debug('addToUploadedFiles: ', uploadedFiles, file, status );

  uploadedFiles = uploadedFiles || [];
  processedFiles = processedFiles || [];
  if (!file) {
    return scrubUploadedFiles(uploadedFiles, processedFiles);
  }
  // Remove a previous entry from the uploadedFiles if it has the same name as the new one
  uploadedFiles = uploadedFiles.filter((uploadedFile) => {return uploadedFile.fileName !== file.name});
  // Add (/replace) the new file, making the structure look the same as for a processed document for easy merging
  uploadedFiles.push({id: null, fileName: file.name, processingDate: status, processedCount: 0, errorCount: 0});

  //If error due to duplicate file, do not remove duplicate file from unprocessed file section
  if(file.error !== undefined && file.error.status === 409)
  {
    return uploadedFiles;
  }

  return scrubUploadedFiles(uploadedFiles, processedFiles);
}

export const updateSubStateFromAction = (state = {}, action) => {
  log.debug('updateSubStateFromAction: ', action, state );
  const getNewState = (action, state) => {
    switch (action.type) {
      case LOAD:
        return Object.assign({}, state, {loading: true, error: null});
      case LOAD_OK:
        return Object.assign({}, state, {processedDocuments: action.response, loading: false, error: null, uploadedFiles: scrubUploadedFiles(state.uploadedFiles, action.response)});
      case LOAD_FAIL:
        return Object.assign({}, state, {processedDocuments: undefined, loading: false, error: action.error});
      case LOAD_DETAILS:
        return Object.assign({}, state, {loading: true, error: null});
      case LOAD_DETAILS_OK:
        return Object.assign({}, state, {documentDetails: action.response, loading: false, error: null});
      case LOAD_DETAILS_FAIL:
        return Object.assign({}, state, {documentDetails: undefined, loading: false, error: action.error});
      case UNLOAD_DETAILS:
        return Object.assign({}, state, {documentDetails: undefined, loading: false, error: null});
      case ACTION_OK:
        return Object.assign({}, state, {loading: false, error: null});
      case ACTION_FAIL:
        return Object.assign({}, state, {loading: false, failures: action.failures});
      // Reacting to File Upload actions to populate the queued files list within the uploadedDocuments sub-state
      case FILE_ACTIONS.FILE_UPLOAD:
      case FILE_ACTIONS.FILE_UPLOAD_OK:
      case FILE_ACTIONS.FILE_UPLOAD_FAIL:
        return Object.assign({}, state, {uploadedFiles: addToUploadedFiles(state.uploadedFiles, state.processedDocuments, action.status, getFileProcessedStatusFromUploadStatus(action.type))});
      default:
        return state;
    }
  };
  const newState = getNewState(action, state);
  log.debug('updateSubStateFromAction:  newState', newState );
  return newState;
};

export const API = {
  doLoadUploadedDocuments: (apiParams = {}) => {
    return async (dispatch) => {
      log.debug('doLoadUploadedDocuments: ');
      dispatch({ type: LOAD });
      try {
        const response = `${apiParams.manualDocumentsOnly}` === 'true'
          ? await CLIENT.getManualDocumentsV2(apiParams)
          : await CLIENT.getProcessedDocumentsV6(apiParams);
        dispatch({ type: LOAD_OK, response });
      } catch (error) {
        log.debug('doLoadUploadedDocuments: error', error);
        dispatch({ type: LOAD_FAIL, error });
      }
    };
  },

  doLoadDocumentDetails: (documentId) => {
    return async (dispatch) => {
      log.debug('doLoadDocumentDetails: ', documentId);
      dispatch({type: LOAD_DETAILS});
      try {
        const response = await CLIENT.getProcessedDocumentDetails(documentId);
        log.debug('doLoadDocumentDetails: ok', response);
        dispatch({type: LOAD_DETAILS_OK, response});
      } catch (error) {
        log.debug('doLoadDocumentDetails: error', error);
        dispatch({type: LOAD_DETAILS_FAIL, error});
      }
    };
  },

  doUnLoadDocumentDetails: () => {
    return async (dispatch) => {
      log.debug('doUnLoadDocumentDetails: ');
      dispatch({type: UNLOAD_DETAILS});
    };
  },

  getDocumentErrorDetails: (fileId) => {
    log.debug('getDocumentErrorDetails: ', fileId);
    return CLIENT.getUploadedDocumentErrorDetails(fileId);
  },

  doOperationOnDocument: (values, cb) => {
    cb = typeof cb === 'function' ? cb : () => {};
    log.clearDictionaryError(ALL_MESSAGES.FILE_OPERATION_STARTED_SUCCESS);
    return async (dispatch) => {
      log.debug('doOperationOnDocument: "%s": ', values.action, values.documentIds);
      try {
        const response = await CLIENT.doActionOnProcessedDocuments(values);
        log.debug('doOperationOnDocument: "%s": documentId: %s): succeeded: ', values.action, values.documentIds, response);
        cb(null, response);
        dispatch({type: ACTION_OK, processedDocuments: values.documentIds});
      } catch (error) {
        log.debug('doOperationOnDocument: "%s": documentId: %s): failed: ', values.action, values.documentIds, error);
        cb(error);
        dispatch({type: ACTION_FAIL, error});
      }
    };
  },
};

export default API;
